// frontend/src/utils/roles.js
/**
 * Role helpers for client-side UI gating.
 *
 * IMPORTANT: All security-relevant decisions are enforced server-side.
 * Hiding a button on the frontend is purely UX -- so users don't see
 * controls that would 403 anyway. The backend remains the source of
 * truth.
 *
 * Roles, in priority order: super_admin > admin > analyst > viewer
 * (super_admin is for Anthropic/Connex internal use, not customer roles)
 */

export function getUserRoles() {
  try {
    return JSON.parse(localStorage.getItem('user_roles') || '[]');
  } catch {
    return [];
  }
}

export function hasRole(role) {
  return getUserRoles().includes(role);
}

export function hasAnyRole(...roles) {
  const userRoles = getUserRoles();
  return roles.some(r => userRoles.includes(r));
}

// Common permission checks used across pages
export const isSuperAdmin = () => hasRole('super_admin');
export const isAdmin = () => hasAnyRole('super_admin', 'admin');
export const isAnalystOrHigher = () => hasAnyRole('super_admin', 'admin', 'analyst');
export const isViewerOrHigher = () => hasAnyRole('super_admin', 'admin', 'analyst', 'viewer');

// What the current user can do (mirrors backend permissions)
export const canCreateConnector = () => isAdmin();
export const canEditConnector = () => isAdmin();
export const canDeleteConnector = () => isAdmin();
export const canSyncConnector = () => isAnalystOrHigher();
export const canManageUsers = () => isAdmin();

// Display name for the user's primary role (for UI labels)
export function primaryRoleLabel() {
  const roles = getUserRoles();
  if (roles.includes('super_admin')) return 'Super Admin';
  if (roles.includes('admin')) return 'Admin';
  if (roles.includes('analyst')) return 'Analyst';
  if (roles.includes('viewer')) return 'Viewer';
  return 'User';
}